import {
  Avatar,
  Box,
  Button,
  DialogActions,
  DialogContent,
  List,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { MEDICATION_STATUSES, postOrders } from 'API';
import { AsyncButton, MedicationList, MedicationListItem } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import {
  useDictionary,
  useIsDesktop,
  usePrescriptionAndUnHandledOrders,
} from 'Hooks';
import { Fragment, useContext, useRef, useState } from 'react';
import { isExpired } from 'Utils';
import { OrderRegularMedicationsContext } from '../OrderRegularMedicationsContext';
import { illegalChars } from 'Regex';
import { sendEvent } from 'Utils/sendEvent';
import { BGNewMobile, LeftArrowWhite, PenIcon } from 'Images';
import { DialogTitleContent } from 'Modules/EquipmentCreateOrder/SummaryOrderDialog/DialogTitleContent';

interface IOrderSummary {
  closeModal: () => void;
}

export const OrderSummary = ({ closeModal }: IOrderSummary) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const isDesktop = useIsDesktop();

  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);

  const textArea = useRef<HTMLInputElement | null>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [orderNoteCount, setOrderNoteCount] = useState(0);
  const LIMIT_NOTES = 100;
  const {
    handleClose,
    checkedMedications,
    outOfStockMedications,
    prevStep,
    openSuccessOrderDialog,
    setSuccessOrderDialogExpiredPrescriptions,
    expiredMedicationOptions,
    isSplit,
    period,
    setNumberOfOrderedItems,
  } = useContext(OrderRegularMedicationsContext);

  const { addOrder } = usePrescriptionAndUnHandledOrders();

  outOfStockMedications.forEach(
    (outOfStockMedication) =>
      (outOfStockMedication.OutOfStockOptionCode =
        expiredMedicationOptions.find(
          (medOption) =>
            medOption.value === outOfStockMedication.lackMedicineNote
        )?.code)
  );

  const orderMedications = checkedMedications.concat(outOfStockMedications);

  const sendOrderHandler = async () => {
    sendEvent('Medicine', 'MedicineOrderButton');
    setIsLoading(true);
    const notToSendMedications = orderMedications.filter(
      (medication) =>
        medication.lackMedicineNote ===
        expiredMedicationOptions.find((option) => option.code === 4)?.value
    );
    let text = '';
    if (notToSendMedications.length) {
      text = notToSendMedications
        .map(
          (medication) =>
            medication.medicatioN_NAME + ' חסרה במלאי בחרת לוותר על התרופה'
        )
        .join(' ');
    }
    const cloneOrderMedications = orderMedications.filter(
      (medication) =>
        medication.lackMedicineNote !==
        expiredMedicationOptions.find((option) => option.code === 4)?.value
    );

    const orders = await postOrders({
      medications: cloneOrderMedications,
      notes: textArea.current ? textArea.current.value + text : text,
      monthsAmount: period,
      isSplitedPerMonths: isSplit,
    });

    if (!orders) {
      setIsLoading(false);
      closeModal();
      return;
    }

    setNumberOfOrderedItems(orders[0]?.medications?.length || 0);

    orders.forEach((order) => {
      addOrder(order);
    });
    const expiredMedications = cloneOrderMedications
      .filter((medication) => isExpired(new Date(medication.nextrefreshdate)))
      .map((medication) => medication.medicatioN_NAME);
    setSuccessOrderDialogExpiredPrescriptions(expiredMedications);
    setIsLoading(false);
    openSuccessOrderDialog();
    handleClose();
  };

  const dialogContentStyle = {
    padding: isDesktop ? '0 32px !important' : '0 24px',
    backgroundColor: isDesktop ? '#FFF' : 'transparent',
    paddingBottom: isDesktop ? undefined : '180px',
    zIndex: 111,
  };

  const dialogTitleStyle = {
    padding: isDesktop ? '0' : '35px 24px 16px 24px',
    background: isDesktop ? '#fff' : `url(${BGNewMobile}) no-repeat`,
    backgroundSize: isDesktop ? undefined : '100%',
    paddingRight: '24px',
    paddingLeft: '24px',
    height: isDesktop ? '92px' : '135px',
  };

  const periodLabel =
    period === 3
      ? dictionary.createOrder.threeMonthsSummery
      : dictionary.createOrder.oneMonthSummery;
  const isSplitLabel =
    isSplit && period === 3
      ? `(${dictionary.createOrder.split})`
      : period === 3
      ? `(${dictionary.createOrder.supplyAll})`
      : '';

  return (
    <>
      <Box sx={dialogTitleStyle} id='summary-order-dialog-title'>
        <DialogTitleContent onClose={handleClose} />
      </Box>
      <DialogContent sx={dialogContentStyle} id='summary-order-dialog-content'>
        <Typography>
          {`${dictionary.youChoseToOrderNumberOfMedications(
            orderMedications.length
          )} ${periodLabel} ${isSplitLabel}`}
        </Typography>
        <Stack mt={1}>
          {period === 3 && (
            <Typography variant='body6'>
              {dictionary.infoBeforeFutureOrder}
            </Typography>
          )}
          <Typography mt={isDesktop ? undefined : 2} variant='body6'>
            {dictionary.infoBeforeOrder}
          </Typography>
        </Stack>

        {isDesktop && (
          <Stack
            flexDirection={'row'}
            sx={{
              p: '8px 8px 20px',
              mt: isDesktop ? '32px' : '15px',
            }}
          >
            <Typography width={'33%'}>
              {dictionary.nameOfTheMedication}
            </Typography>
            <Typography width={'33%'}>{dictionary.dosageTitle}</Typography>
            <Typography width={'33%'}>{dictionary.notes}</Typography>
          </Stack>
        )}
        <List sx={{ p: 0, mb: '56px' }}>
          {isDesktop ? (
            <MedicationList medications={orderMedications} />
          ) : (
            <Paper
              sx={{
                mt: '16px',
                mb: '0',
                pb: '1px',
                width: '100%',
                boxShadow: 'unset',
              }}
            >
              {orderMedications.map(
                (medication, index) =>
                  medication.OutOfStockOptionCode !==
                    MEDICATION_STATUSES.notAvailable && (
                    <Fragment key={medication.medicatioN_CODE}>
                      <MedicationListItem
                        checkedMedications={checkedMedications}
                        handleToggle={() => () => {}}
                        medication={medication}
                        outOfStockMedications={outOfStockMedications}
                        showMedicationStatus={true}
                        key={index}
                        index={index}
                        isFinalStep
                      />
                    </Fragment>
                  )
              )}
            </Paper>
          )}
        </List>

        <Typography
          fontWeight={700}
          variant='h5'
          bottom={'20px'}
          position={'relative'}
          component='label'
          htmlFor='general-notes'
        >
          {dictionary.orderComments}
        </Typography>
        <TextField
          inputRef={textArea}
          InputProps={{
            id: 'general-notes',
            sx: {
              borderRadius: '6px',
              height: isDesktop ? undefined : '120px',
            },
          }}
          multiline
          rows={3}
          sx={{
            backgroundColor: '#fff',
            borderRadius: '6px',

            '.MuiInputBase-formControl': {
              borderRadius: '6px',
              backgroundColor: '#fff',
            },
          }}
          placeholder={dictionary.orderCommentsPlaceHolder}
          minRows={5}
          fullWidth
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value.replace(illegalChars, '');
            e.target.value =
              newValue.length > LIMIT_NOTES
                ? newValue.substring(0, LIMIT_NOTES)
                : newValue;
            setTimeout(() => {
              setOrderNoteCount(e.target.value.length);
            }, 1000);
          }}
        />
        <Typography
          sx={{
            fontSize: '16px !important',
            position: 'relative',
            top: '-30px',
            ml: 'auto',
            p: '0 15px',
            color: 'purple2.main',
          }}
          variant='body7'
          aria-live='assertive'
        >
          {orderNoteCount}/{100}
        </Typography>
      </DialogContent>

      <DialogActions
        sx={
          isDesktop
            ? {
                backgroundColor: '#fff',
                padding: '40px',
              }
            : {
                justifyContent: 'center',
                backgroundColor: '#fff',
                flexDirection: 'row',
                position: 'fixed',
                bottom: 0,
                width: '100%',
                pb: '15px',
              }
        }
      >
        <Button
          sx={{
            display: isLoading ? 'none' : undefined,
            p: isDesktop ? '8px 48px' : '4px 16px',
            width: isDesktop ? '230px' : '50%',
            fontSize: isDesktop ? '18px' : '16px',
            fontWeight: 700,
          }}
          startIcon={
            <Avatar
              src={PenIcon}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
              }}
            />
          }
          variant='outlined'
          onClick={() => prevStep()}
        >
          {generalDictionary.editOrder}
        </Button>
        <AsyncButton
          loading={isLoading}
          variant='contained'
          sx={
            isDesktop
              ? {
                  padding: '8px 48px',
                  width: isDesktop ? '230px' : '50%',
                  fontSize: '18px',
                  fontWeight: 600,
                  ml: '32px !important',
                }
              : {
                  fontSize: '16px',
                  fontWeight: 600,
                  padding: '5px 24px',
                }
          }
          onClick={sendOrderHandler}
          endIcon={
            <Avatar
              src={LeftArrowWhite}
              alt=''
              sx={{
                width: '21px',
                height: '20px',
              }}
            />
          }
        >
          {generalDictionary.sendOrder}
        </AsyncButton>
      </DialogActions>
    </>
  );
};
